import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DeliverableDetailPopup.module.scss';
import InfoHint from '../../ui/InfoHint/InfoHint';
import {
  IconX,
  IconTrash,
  IconPencil,
  IconCheck,
  IconDeviceFloppy,
  IconExternalLink,
  IconPlus,
  IconTag
} from '@tabler/icons-react';
import {
  useGetTaskIdDeliverobject
} from '../../../api/task-api/task-api';
import { useGetDeliverObjectId, useDeleteDeliverObjectId } from '../../../api/deliver-object-api/deliver-object-api';
import {useGetLabels, useGetLabelsSuggest, usePostLabels, useDeleteLabelsId, usePutLabelsId} from '../../../api/label-api/label-api';
import {
  TaskDto,
  TaskStatus,
  PhaseDto,
  PhaseType,
  DeliverObjectCriticality,
  Methods,
  ProjectDto,
  LabelDto,
  DeliverObjectDto
} from '../../../api/model';
import {getPhaseDisplayName} from '../../../utils/phaseUtils';
import { Deliverable } from '../../../types/deliverables';
import { useProjectPhases } from '../../../hooks/useProjectPhases';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePutDeliverObjectUpdate } from '../../../hooks/usePutDeliverObjectUpdate';
import { useQueryClient } from '@tanstack/react-query';
import { useProjectReleases } from '../../../hooks/useProjectReleases';
import { useGetMilestoneIdProject } from '../../../api/milestone-api/milestone-api';
import { getSprint } from '../../../api/sprint-api/sprint-api';
import { SprintDto } from '../../../api/model';
import CreateTaskModal from '../../Tasks/CreateTaskModal';
import EditTaskModal from '../../Tasks/EditTaskModal';
import DependencySection from '../../Dependencies/DependencySection';
import ConfirmationDialog from '../../ui/ConfirmationDialog/ConfirmationDialog';
import CompletionDonut from '../../ui/CompletionDonut/CompletionDonut';
import { useGetIntelligenceProjectProjectIdTaskCompletion } from '../../../api/intelligence-api/intelligence-api';
import { useGetProjectIdMembers } from '../../../api/project-api/project-api';
import UserPicker from '../../ui/UserPicker/UserPicker';
import { memberAsUser } from '../../../utils/projectMembers';
import TextLimitHint from '../../ui/TextLimitHint/TextLimitHint';
import { SHORT_DESCRIPTION } from '../../../utils/textLimits';

// interface for deliverable (for extended ones aswell)
interface ExtendedDeliverable extends Omit<Deliverable, 'name'> {
  name: string;
  criticality?: DeliverObjectCriticality;
  isExtended?: boolean;
  phaseId?: number;
}

interface Task {
  id: string | number;
  name: string;
  description?: string;
  status: TaskStatus | 'completed' | 'pending';
  progress?: number;
}

interface DeliverableDetailPopupProps {
  isOpen: boolean;
  onClose: () => void;
  deliverable: Deliverable;
  projectDeliverables?: any[];
  rawDeliverables?: DeliverObjectDto[];
  onDeliverableUpdate?: (updatedDeliverable: any) => void;
  projectId?: number;
  project?: ProjectDto;
  onNavigateToTask?: (taskId: number) => void;
}

const DeliverableDetailPopup: React.FC<DeliverableDetailPopupProps> = ({
  isOpen,
  onClose,
  deliverable,
  projectDeliverables = [],
  rawDeliverables,
  onDeliverableUpdate,
  projectId,
  project,
  onNavigateToTask
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [progress, setProgress] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [criticality, setCriticality] = useState<DeliverObjectCriticality | null>(null);
  const [isExtended, setIsExtended] = useState(false);
  const [phaseId, setPhaseId] = useState<number | undefined>(undefined);
  const [releaseId, setReleaseId] = useState<number | undefined>(undefined);
  const [sprintId, setSprintId] = useState<number | undefined>(undefined);
  const [milestoneId, setMilestoneId] = useState<number | undefined>(undefined);
  const [responsibleUserId, setResponsibleUserId] = useState<number | undefined>(undefined);
  const [labels, setLabels] = useState<LabelDto[]>([]);
  const [labelSearch, setLabelSearch] = useState('');
  const [isLabelDropdownOpen, setIsLabelDropdownOpen] = useState(false);
  const [newLabelColor, setNewLabelColor] = useState('#3b82f6');
  const [labelToDelete, setLabelToDelete] = useState<LabelDto | null>(null);
  const [labelToEdit, setLabelToEdit] = useState<LabelDto | null>(null);
  const [editLabelName, setEditLabelName] = useState('');
  const [editLabelColor, setEditLabelColor] = useState('#3b82f6');

  // Deliverable deletion state
  const [isConfirmingDeliverableDelete, setIsConfirmingDeliverableDelete] = useState(false);
  const [isDeletingDeliverable, setIsDeletingDeliverable] = useState(false);
  
  // Create task modal state
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  
  // Edit task modal state
  const [selectedTaskForEdit, setSelectedTaskForEdit] = useState<TaskDto | null>(null);
  
  const [isSaving, setIsSaving] = useState(false);
  const [showScheduleWarning, setShowScheduleWarning] = useState(false);
  const [pendingSaveFn, setPendingSaveFn] = useState<(() => void) | null>(null);
  
  // cache invalidation fixing
  const queryClient = useQueryClient();
  
  // Fetch project phases our hook
  const {
    data: projectPhases = [],
    isLoading: isLoadingPhases
  } = useProjectPhases(projectId);

  // Fetch project releases
  const { data: projectReleases = [], isLoading: isLoadingReleases, refetch: refetchReleases } = useProjectReleases(projectId);
  const { data: projectMilestones = [] } = useGetMilestoneIdProject(projectId!, { query: { enabled: !!projectId } });
  const { data: projectMembers } = useGetProjectIdMembers(projectId ?? 0, { query: { enabled: !!projectId } });
  const memberUsers = useMemo(() => (projectMembers ?? []).map(memberAsUser), [projectMembers]);

  // Fetch all sprints
  const [allSprints, setAllSprints] = useState<SprintDto[]>([]);
  const [isLoadingSprints, setIsLoadingSprints] = useState(false);

  // Check if project is classic
  const isClassicProject = project?.method === Methods.CLASSIC;
  
  const { t } = useTranslation('deliverables');
  const deleteDeliverableMutation = useDeleteDeliverObjectId();
  const { notifySuccess, notifyError } = useNotifications();
  const createLabelMutation = usePostLabels();
  const deleteLabelMutation = useDeleteLabelsId();
  const updateLabelMutation = usePutLabelsId();

  const {data: labelSuggestions = []} = useGetLabelsSuggest(
      {query: labelSearch, projectId},
      {query: {enabled: isLabelDropdownOpen && labelSearch.length > 0 && !!projectId}}
  );
  const {data: allLabels = []} = useGetLabels(
      {projectId},
      {query: {enabled: isLabelDropdownOpen && !!projectId}}
  );

  const sourceLabels = labelSearch.length > 0 ? labelSuggestions : allLabels;

  const filteredSuggestions = sourceLabels.filter(
      s => !labels.some(l => l.id === s.id)
  );

  const hasExactMatch = sourceLabels.some(
      s => s.name?.toLowerCase() === labelSearch.toLowerCase()
  );

  const getContrastColor = (hex?: string): string => {
    if (!hex || hex.length < 7) return '#2e2e32';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#2e2e32' : '#ffffff';
  };

  const handleAddLabel = (label: LabelDto) => {
    if (!labels.some(l => l.id === label.id)) {
      setLabels(prev => [...prev, label]);
    }
    setLabelSearch('');
    setIsLabelDropdownOpen(false);
    setLabelToEdit(null);
  };

  const handleRemoveLabel = (labelId: number) => {
    setLabels(prev => prev.filter(l => l.id !== labelId));
  };

  const handleCreateLabel = async () => {
    if (!labelSearch.trim() || !projectId) return;
    try {
      const newLabel = await createLabelMutation.mutateAsync({
        data: {name: labelSearch.trim(), color: newLabelColor, projectId}
      });
      handleAddLabel(newLabel);
      setNewLabelColor('#3b82f6');
    } catch {
      notifyError(t('detail.toast.labelErrorTitle'), t('detail.toast.labelError'));
    }
  };

  const invalidateLabelCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['/labels'] });
    queryClient.invalidateQueries({ queryKey: ['/labels/suggest'] });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey?.[0];
        return typeof key === 'string' &&
          (key.startsWith('/deliver-object') || key.startsWith('/phase'));
      },
    });
  };

  // Delete a label from its project
  const handleConfirmDeleteLabel = async () => {
    const target = labelToDelete;
    if (!target?.id) return;
    try {
      await deleteLabelMutation.mutateAsync({ id: target.id });
      setLabels(prev => prev.filter(l => l.id !== target.id));
      invalidateLabelCaches();
      notifySuccess(t('detail.toast.labelDeleteTitle'), t('detail.toast.labelDeleteSuccess', { name: target.name }));
    } catch {
      notifyError(t('detail.toast.labelDeleteTitle'), t('detail.toast.labelDeleteError'));
    }
  };

  const handleStartEditLabel = (label: LabelDto) => {
    setLabelToEdit(label);
    setEditLabelName(label.name || '');
    setEditLabelColor(label.color || '#3b82f6');
  };

  const handleCancelEditLabel = () => {
    setLabelToEdit(null);
    setEditLabelName('');
  };

  // Rename / recolor an existing label
  const handleConfirmEditLabel = async () => {
    const target = labelToEdit;
    const name = editLabelName.trim();
    if (!target?.id || !name) return;
    try {
      const updated = await updateLabelMutation.mutateAsync({
        id: target.id,
        data: { name, color: editLabelColor, projectId: target.projectId },
      });
      // Reflect the change on this deliverable
      setLabels(prev => prev.map(l => (l.id === target.id ? { ...l, ...updated } : l)));
      invalidateLabelCaches();
      handleCancelEditLabel();
      notifySuccess(t('detail.toast.labelEditTitle'), t('detail.toast.labelEditSuccess', { name }));
    } catch {
      notifyError(t('detail.toast.labelEditTitle'), t('detail.toast.labelEditError'));
    }
  };

  // Setup deliverable update
  const updateDeliverableMutation = usePutDeliverObjectUpdate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/deliver-object'] });
        // Cache optimize
        if (numericDeliverableId) { 
          queryClient.invalidateQueries({ queryKey: [`/deliver-object/${numericDeliverableId}`] });
        }
        if (projectId) {
          queryClient.invalidateQueries({ queryKey: [`/phase/${projectId}/project`] });
        }
      }
    }
  });
  
  // convert table id to real ids
  const numericDeliverableId = useMemo(() => {
    return deliverable.id 
      ? (typeof deliverable.id === 'string' ? parseInt(deliverable.id, 10) : deliverable.id)
      : 0;
  }, [deliverable.id]);

  const shouldFetchTasks = useMemo(() => {
    return isOpen && numericDeliverableId !== undefined && numericDeliverableId > 0;
  }, [isOpen, numericDeliverableId]);

  const { data: taskCompletion } = useGetIntelligenceProjectProjectIdTaskCompletion(
    projectId ?? 0,
    { query: { enabled: !!projectId } }
  );
  const completion = useMemo(
    () => taskCompletion?.deliverables?.find(c => c.scopeId === numericDeliverableId),
    [taskCompletion, numericDeliverableId]
  );

  // Fetch deliverable data
  const {
    data: latestDeliverableData,
    isLoading: isLoadingDeliverable
  } = useGetDeliverObjectId(
    numericDeliverableId || 0,
    {
      query: {
        enabled: shouldFetchTasks,
        refetchOnMount: true,
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        staleTime: 0,
        gcTime: 10 * 60 * 1000,
      }
    }
  );

  const { 
    data: apiTasks = [], 
    isLoading: isLoadingTasks, 
    error: tasksError,
    refetch: refetchTasks
  } = useGetTaskIdDeliverobject(
      numericDeliverableId || 0,
    {
      query: {
        enabled: shouldFetchTasks,
        retry: 2,
          staleTime: 0,
        gcTime: 5 * 60 * 1000,
          refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchInterval: false,
      }
    }
  );

  // Map api tasks to our task format when they are loaded
  useEffect(() => {
    if (apiTasks && apiTasks.length > 0) {
      const formattedTasks: Task[] = apiTasks.map((task: TaskDto, index: number) => ({
        id: task.id || `temp-task-${index}`,
        name: task.name || 'Unnamed Task',
        description: task.description || '',
        status: task.status || TaskStatus.PLANNED,
        progress: task.progress || 0
      }));
      setTasks(formattedTasks);
    } else {
      // If no tasks from api return empty array
      setTasks([]);
    }
  }, [apiTasks]);
  
  // Fetch sprints when detail modal popup opens
  useEffect(() => {
    const fetchSprints = async () => {
      if (!isOpen || isClassicProject) {
        setAllSprints([]);
        return;
      }
      
      setIsLoadingSprints(true);
      try {
        const sprintsData = await getSprint();
        setAllSprints(sprintsData || []);
      } catch (error) {
        console.error('Error fetching sprints:', error);
        setAllSprints([]);
      } finally {
        setIsLoadingSprints(false);
      }
    };
    
    fetchSprints();
  }, [isOpen, isClassicProject]);

  // Clean up all form state when the component opens or closes
  useEffect(() => {
    if (!isOpen) {
      setTasks([]);
      setName('');
      setProgress(0);
      setCriticality(null);
      setIsExtended(false);
      setPhaseId(undefined);
      setReleaseId(undefined);
      setSprintId(undefined);
      setMilestoneId(undefined);
      setStartDate('');
      setEndDate('');
      setLabels([]);
      setLabelSearch('');
      setLabelToEdit(null);
      setIsLabelDropdownOpen(false);
      return;
    }
    
    // Refetch releases when user opens popup
    if (isOpen && projectId) {
      refetchReleases();
    }
  }, [isOpen, projectId, refetchReleases]);
  
  // deliverable data population
  useEffect(() => {
    if (!isOpen || !deliverable) return;
    
    const dataSource = latestDeliverableData || deliverable;
    
    if (dataSource) {
      try {
        const deliverableData = dataSource as any;

        // Only update if values are different to prevent unnecessary re-renders
        const newName = deliverableData.name || '';
        const newProgress = typeof deliverableData.progress === 'number' ? deliverableData.progress : 0;
        const newCriticality = deliverableData.criticality || null;
        const newIsExtended = Boolean(deliverableData.isExtended || deliverableData.extended);
        const deliverablePhaseId = deliverableData.phaseId || deliverableData.phase_id;
        const newPhaseId = deliverablePhaseId ? Number(deliverablePhaseId) : undefined;
        const newReleaseId = deliverableData.releaseId ? Number(deliverableData.releaseId) : undefined;
        const newSprintId = deliverableData.sprint ? Number(deliverableData.sprint) : undefined;
        const newMilestoneId = deliverableData.milestoneId ? Number(deliverableData.milestoneId) : undefined;
        const newResponsibleUserId = deliverableData.responsibleUserId ? Number(deliverableData.responsibleUserId) : undefined;
        
        // Date formatting
        const newStartDate = deliverableData.startDate
          ? (typeof deliverableData.startDate === 'string' ? deliverableData.startDate.split('T')[0] : '')
          : '';
        const newEndDate = deliverableData.endDate 
          ? (typeof deliverableData.endDate === 'string' ? deliverableData.endDate.split('T')[0] : '')
          : '';

        // Batch all state updates
        setName(newName);
        setDescription(deliverableData.description || '');
        setProgress(newProgress);
        setCriticality(newCriticality);
        setIsExtended(newIsExtended);
        setPhaseId(newPhaseId);
        setReleaseId(newReleaseId);
        setSprintId(newSprintId);
        setMilestoneId(newMilestoneId);
        setResponsibleUserId(newResponsibleUserId);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setLabels(deliverableData.labels || []);
      } catch (error) {
        console.error('Error setting deliverable data:', error);
        // Reset to defaults if errors
        setName('');
        setProgress(0);
        setStartDate('');
        setEndDate('');
        setCriticality(null);
        setIsExtended(false);
        setPhaseId(undefined);
        setReleaseId(undefined);
        setSprintId(undefined);
        setMilestoneId(undefined);
      }
    }
  }, [latestDeliverableData, deliverable, isOpen]);

  // Escape closes the innermost open thing: the delete confirmation, else the
  // popup - but never while a dialog on top of it (task, confirmation) is open,
  // and not when a field already used the key (label edit, pickers).
  const nestedDialogOpen = isCreateTaskOpen || !!selectedTaskForEdit || !!labelToDelete || showScheduleWarning;
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || nestedDialogOpen) return;
      if (isConfirmingDeliverableDelete) {
        setIsConfirmingDeliverableDelete(false);
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, nestedDialogOpen, isConfirmingDeliverableDelete, onClose]);

  if (!isOpen) {
    return null;
  }

  const displayData = (latestDeliverableData || deliverable) as any;

  const isOutsideParentTimeline = (): boolean => {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (phaseId) {
      const phase = projectPhases.find((p: PhaseDto) => p.id === phaseId);
      if (phase?.startTime && phase?.endTime) {
        const ps = new Date(phase.startTime);
        const pe = new Date(phase.endTime);
        if (start < ps || end > pe) return true;
      }
    }
    if (sprintId && !isClassicProject) {
      const sprint = allSprints.find(s => s.id === sprintId);
      if (sprint?.startDate && sprint?.endDate) {
        const ss = new Date(sprint.startDate);
        const se = new Date(sprint.endDate);
        if (start < ss || end > se) return true;
      }
    }
    if (releaseId) {
      const release = projectReleases.find((r: any) => r.id === releaseId);
      if (release?.startDate && release?.endDate) {
        const rs = new Date(release.startDate);
        const re = new Date(release.endDate);
        if (start < rs || end > re) return true;
      }
    }
    return false;
  };

  const doSave = async () => {
    setIsSaving(true);
    try {
      const deliverableData = deliverable as any;
      const updateData: any = {
        id: numericDeliverableId,
        name: name || deliverableData.name || '',
        // sent even when emptied, so clearing it sticks
        description,
        progress: progress || deliverableData.progress || 0,
        isExtended: isExtended !== undefined ? isExtended : deliverableData.isExtended || false
      };
      if (criticality) updateData.criticality = criticality;
      updateData.phaseId = phaseId;
      updateData.releaseId = releaseId;
      updateData.sprint = sprintId;
      updateData.milestoneId = milestoneId;
      updateData.responsibleUserId = responsibleUserId;
      updateData.labels = labels;
      if (startDate || deliverableData.startDate) {
        updateData.startDate = startDate || (typeof deliverableData.startDate === 'string' ? deliverableData.startDate.split('T')[0] : deliverableData.startDate);
      }
      if (endDate || deliverableData.endDate) {
        updateData.endDate = endDate || (typeof deliverableData.endDate === 'string' ? deliverableData.endDate.split('T')[0] : deliverableData.endDate);
      }
      if (numericDeliverableId) {
        await updateDeliverableMutation.mutateAsync({ id: numericDeliverableId, data: updateData });
        notifySuccess(t('detail.toast.updateTitle'), t('detail.toast.updateSuccess'));
      }
      if (onDeliverableUpdate) {
        onDeliverableUpdate({ ...deliverable, ...updateData, id: numericDeliverableId });
      }
      onClose();
    } catch (error) {
      console.error('Error saving deliverable:', error);
      notifyError(t('detail.toast.updateTitle'), error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    // Validate name
    if (name && name.length > 80) {
      notifyError(t('detail.validation.error'), t('detail.validation.nameTooLong'));
      return;
    }
    // Validate dates
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end < start) {
        notifyError(t('detail.validation.error'), t('detail.validation.endBeforeStart'));
        return;
      }
    }
    // Soft warning
    if (isOutsideParentTimeline()) {
      setPendingSaveFn(() => doSave);
      setShowScheduleWarning(true);
      return;
    }
    await doSave();
  };


  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };



  // Delete confirm modal handlers
  const handleShowDeliverableDeleteConfirmation = () => {
    setIsConfirmingDeliverableDelete(true);
  };

  // Cancel delete confirmation
  const handleCancelDeliverableDelete = () => {
    setIsConfirmingDeliverableDelete(false);
  };

  // Confirm delete deliverable
  const handleConfirmDeleteDeliverable = async () => {
    if (!numericDeliverableId) return;

    setIsDeletingDeliverable(true);
    
    try {
      await deleteDeliverableMutation.mutateAsync({ id: numericDeliverableId });
      
      notifySuccess(t('detail.toast.deleteTitle'), t('detail.toast.deleteSuccess', { name }));
      
      // refresh table data
      queryClient.invalidateQueries({ queryKey: ['/deliver-object'] });
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: [`/phase/${projectId}/project`] });
      }
      if (phaseId) {
        queryClient.invalidateQueries({ queryKey: [`/deliver-object/${phaseId}/phase`] });
      }

      // Close popup
      onClose();
    } catch (error) {
      console.error('Error deleting deliverable:', error);
      notifyError(t('detail.toast.deleteTitle'), error);
    } finally {
      setIsDeletingDeliverable(false);
      setIsConfirmingDeliverableDelete(false);
    }
  };

  const handleNavigateToTask = (taskId: number) => {
    const taskDto = apiTasks.find((t: TaskDto) => t.id === taskId);
    if (taskDto) {
      setSelectedTaskForEdit(taskDto);
    } else if (onNavigateToTask) {
      onClose();
      onNavigateToTask(taskId);
    }
  };

  // Helper function to map status to display label
  const getStatusLabel = (status?: TaskStatus | string) => {
    switch (status) {
      case TaskStatus.FINISHED:
      case 'completed':
        return t('detail.status.finished');
      case TaskStatus.IN_PROGRESS:
        return t('detail.status.inProgress');
      case TaskStatus.IN_REVIEW:
        return t('detail.status.inReview');
      case TaskStatus.CANCELLED:
        return t('detail.status.cancelled');
      case TaskStatus.PLANNED:
        return t('detail.status.planned');
      default:
        return 'Not Started';
    }
  };

  // Check if task is completed
  const isTaskCompleted = (status?: TaskStatus | string) => {
    return status === TaskStatus.FINISHED || status === 'completed';
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.popup}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
              <h2 className={styles.title}>{(deliverable.name || t('detail.titleFallback')).replaceAll('_', ' ')}</h2>
            
            <div className={styles.actionArea}>
              <button className={styles.saveButton} onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <div className={styles.savingSpinner}></div>
                    {t('detail.saving')}
                  </>
                ) : (
                  <>
                    <IconDeviceFloppy size={16} />
                    {t('detail.save')}
                  </>
                )}
              </button>
              <button 
                className={styles.deleteButton} 
                onClick={handleShowDeliverableDeleteConfirmation}
                disabled={isDeletingDeliverable}
                title={t('detail.deleteTooltip')}
              >
                <IconTrash size={16} />
                {t('detail.delete')}
              </button>
              <IconX className={styles.actionIcon} onClick={onClose} />
            </div>
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.leftContent}>
            <div className={styles.mainContent}>
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('detail.sectionTitle')}</h3>
                
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t('detail.fields.name')}</label>
                    <input 
                      type="text"
                      className={styles.input}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={80}
                      placeholder={deliverable.name || "Enter deliverable name"}
                    />
                  </div>

                  <div className={styles.formGroup} style={{ gridColumn: '1 / -1' }}>
                    <label className={styles.label}>{t('detail.fields.description')}</label>
                    <textarea
                      className={`${styles.input} ${styles.textarea}`}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={SHORT_DESCRIPTION}
                      rows={3}
                      placeholder={t('detail.fields.descriptionPlaceholder')}
                    />
                    <TextLimitHint value={description} max={SHORT_DESCRIPTION} />
                  </div>


                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t('detail.fields.progress')}</label>
                    <div className={styles.progressContainer}>
                      <div className={styles.progressBar} style={{ width: `${progress}%`, height: '10px', background: '#4caf50', borderRadius: '5px' }}></div>
                      <span className={styles.progressValue}>{progress}%</span>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t('detail.fields.criticality')}<InfoHint text={t('fieldHints.criticality', { ns: 'common' })} /></label>
                    <select 
                      className={styles.select}
                      value={criticality || ''}
                      onChange={(e) => setCriticality(e.target.value ? e.target.value as DeliverObjectCriticality : null)}
                    >
                      <option value="">{t('detail.fields.notAssigned')}</option>
                      <option value={DeliverObjectCriticality.EASY}>{t('criticality.easy')}</option>
                      <option value={DeliverObjectCriticality.MIDDLE}>{t('criticality.middle')}</option>
                      <option value={DeliverObjectCriticality.HIGH}>{t('criticality.high')}</option>
                    </select>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t('detail.fields.phase')}</label>
                    <select
                      className={styles.select}
                      value={phaseId || ""}
                      onChange={(e) => setPhaseId(e.target.value ? parseInt(e.target.value) : undefined)}
                    >
                      <option value="">{t('detail.fields.selectPhase')}</option>
                      {projectPhases.map((phase: PhaseDto) => (
                          <option key={phase.id} value={phase.id}>
                              {getPhaseDisplayName(phase)} {phase.subTypeAgile ? `- ${phase.subTypeAgile}` : ''}
                          </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t('detail.fields.startDate')}</label>
                    <div className={styles.datePickerContainer}>
                      <input 
                        type="date"
                        className={styles.datePicker}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t('detail.fields.endDate')}</label>
                    <div className={styles.datePickerContainer}>
                      <input 
                        type="date"
                        className={styles.datePicker}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate || undefined}
                      />
                    </div>
                    {startDate && endDate && new Date(endDate) < new Date(startDate) && (
                      <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                        {t('detail.validation.endBeforeStart')}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t('detail.fields.release')}<InfoHint text={t('fieldHints.release', { ns: 'common' })} /></label>
                    <select
                      className={styles.select}
                      value={releaseId || ""}
                      onChange={(e) => setReleaseId(e.target.value ? parseInt(e.target.value) : undefined)}
                      disabled={isLoadingReleases}
                    >
                      <option value="">{t('detail.fields.noRelease')}</option>
                      {projectReleases.map((release) => (
                        <option key={release.id} value={release.id}>
                          {release.name} {release.releaseDate ? `- ${release.releaseDate}` : ''}
                        </option>
                      ))}
                    </select>
                    {isLoadingReleases && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {t('detail.fields.loadingReleases')}
                      </div>
                    )}
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t('detail.fields.milestone')}<InfoHint text={t('fieldHints.milestone', { ns: 'common' })} /></label>
                    <select
                      className={styles.select}
                      value={milestoneId || ""}
                      onChange={(e) => setMilestoneId(e.target.value ? parseInt(e.target.value) : undefined)}
                    >
                      <option value="">{t('detail.fields.noMilestone')}</option>
                      {projectMilestones.map((milestone) => (
                        <option key={milestone.id} value={milestone.id}>
                          {milestone.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t('detail.fields.responsible')}</label>
                    <UserPicker
                      value={responsibleUserId ?? null}
                      onChange={setResponsibleUserId}
                      users={memberUsers}
                      placeholder={t('table.notAssigned')}
                    />
                  </div>
                  {!isClassicProject && (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>{t('detail.fields.sprint')}<InfoHint text={t('fieldHints.sprint', { ns: 'common' })} /></label>
                      <select
                        className={styles.select}
                        value={sprintId || ""}
                        onChange={(e) => setSprintId(e.target.value ? parseInt(e.target.value) : undefined)}
                        disabled={isLoadingSprints}
                      >
                        <option value="">{t('detail.fields.noSprint')}</option>
                        {allSprints.map((sprint) => (
                          <option key={sprint.id} value={sprint.id}>
                            {sprint.name} {sprint.startDate ? `- ${sprint.startDate}` : ''}
                          </option>
                        ))}
                      </select>
                      {isLoadingSprints && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {t('detail.fields.loadingSprints')}
                        </div>
                      )}
                    </div>
                  )}

                  <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                    <label className={styles.label}>
                      <IconTag size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: 4}}/>
                      {t('detail.fields.labels')}
                    </label>

                    {labels.length > 0 && (
                        <div className={styles.labelChips}>
                          {labels.map(label => (
                              <span
                                  key={label.id}
                                  className={styles.labelChip}
                                  style={{
                                    backgroundColor: label.color || '#e5e7eb',
                                    color: getContrastColor(label.color)
                                  }}
                              >
                            {label.name}
                                <button
                                    type="button"
                                    className={styles.labelChipRemove}
                                    onClick={() => handleRemoveLabel(label.id!)}
                                    style={{color: getContrastColor(label.color)}}
                                >
                              <IconX size={11}/>
                            </button>
                          </span>
                          ))}
                        </div>
                    )}

                    <div className={styles.labelAutocomplete}>
                      <input
                          type="text"
                          className={styles.input}
                          placeholder={t('detail.fields.labelSearch')}
                          value={labelSearch}
                          onChange={(e) => {
                            setLabelSearch(e.target.value);
                            setIsLabelDropdownOpen(true);
                          }}
                          onFocus={() => setIsLabelDropdownOpen(true)}
                          onBlur={() => setTimeout(() => {
                            setIsLabelDropdownOpen((prev) => (labelToEdit ? prev : false));
                          }, 150)}
                      />

                      {isLabelDropdownOpen && (labelSearch.length > 0 || filteredSuggestions.length > 0) && (
                          <div className={styles.labelDropdown}>
                            {filteredSuggestions.map(label => (
                                labelToEdit?.id === label.id ? (
                                  <div key={label.id} className={styles.labelDropdownItem} onMouseDown={(e) => e.preventDefault()}>
                                    <input
                                        type="color"
                                        value={editLabelColor}
                                        onChange={(e) => setEditLabelColor(e.target.value)}
                                        className={styles.colorPicker}
                                        title={t('detail.fields.labelPickColor')}
                                    />
                                    <input
                                        type="text"
                                        className={styles.labelEditInput}
                                        value={editLabelName}
                                        autoFocus
                                        onChange={(e) => setEditLabelName(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') { e.preventDefault(); handleConfirmEditLabel(); }
                                          if (e.key === 'Escape') { e.preventDefault(); handleCancelEditLabel(); }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className={styles.labelEditConfirmButton}
                                        title={t('detail.fields.labelEditSave')}
                                        disabled={updateLabelMutation.isPending || !editLabelName.trim()}
                                        onClick={(e) => { e.stopPropagation(); handleConfirmEditLabel(); }}
                                    >
                                      <IconCheck size={14}/>
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.labelDeleteButton}
                                        title={t('detail.fields.labelEditCancel')}
                                        onClick={(e) => { e.stopPropagation(); handleCancelEditLabel(); }}
                                    >
                                      <IconX size={14}/>
                                    </button>
                                  </div>
                                ) : (
                                <div key={label.id} className={styles.labelDropdownItem}>
                                  <span
                                      className={styles.labelDropdownItemMain}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleAddLabel(label);
                                      }}
                                  >
                                    <span
                                        className={styles.labelColorDot}
                                        style={{backgroundColor: label.color || '#e5e7eb'}}
                                    />
                                    {label.name}
                                  </span>
                                  <button
                                      type="button"
                                      className={styles.labelEditButton}
                                      title={t('detail.fields.labelEdit')}
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartEditLabel(label);
                                      }}
                                  >
                                    <IconPencil size={14}/>
                                  </button>
                                  <button
                                      type="button"
                                      className={styles.labelDeleteButton}
                                      title={t('detail.fields.labelDelete')}
                                      // Keep focus
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setLabelToDelete(label);
                                      }}
                                  >
                                    <IconTrash size={14}/>
                                  </button>
                                </div>
                                )
                            ))}

                            {labelSearch && !hasExactMatch && (
                                <div className={styles.labelCreateRow} onMouseDown={(e) => e.preventDefault()}>
                                  <span className={styles.labelCreateText}>Create "{labelSearch}"</span>
                                  <input
                                      type="color"
                                      value={newLabelColor}
                                      onChange={(e) => setNewLabelColor(e.target.value)}
                                      className={styles.colorPicker}
                                      title={t('detail.fields.labelPickColor')}
                                      onClick={(e) => e.stopPropagation()}
                                  />
                                  <button
                                      type="button"
                                      className={styles.createLabelButton}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleCreateLabel();
                                      }}
                                      disabled={createLabelMutation.isPending}
                                  >
                                    {createLabelMutation.isPending ? '...' : t('detail.fields.labelCreate')}
                                  </button>
                                </div>
                            )}

                            {labelSearch.length > 0 && filteredSuggestions.length === 0 && hasExactMatch && (
                                <div className={styles.labelDropdownEmpty}>{t('detail.fields.labelAlreadyAdded')}</div>
                            )}

                            {labelSearch.length === 0 && filteredSuggestions.length === 0 && (
                                <div className={styles.labelDropdownEmpty}>{t('detail.fields.labelTypeToSearch')}</div>
                            )}
                          </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>{t('detail.tasks.title')}</h3>
                  <button
                    className={styles.addTaskButton}
                    onClick={() => setIsCreateTaskOpen(true)}
                    title={t('detail.tasks.createTooltip')}
                    type="button"
                  >
                    <IconPlus size={16} />
                    <span>{t('detail.tasks.add')}</span>
                  </button>
                </div>
                
                <div className={styles.taskList}>
                  {isLoadingTasks && (
                    <div className={styles.loadingState}>{t('detail.tasks.loading')}</div>
                  )}
                  
                  {!!tasksError && (
                    <div className={styles.errorState}>
                      {t('detail.tasks.error')}
                    </div>
                  )}
                  
                  {!isLoadingTasks && !tasksError && tasks.length > 0 && tasks.map(task => (
                    <div key={task.id} className={styles.taskItem}>
                      <div className={styles.taskContent}>
                        <div className={styles.taskName}>
                            {task.name.replaceAll('_', ' ')}
                        </div>
                        {task.description && (
                          <div className={styles.taskDescription}>
                              {task.description.replaceAll('_', ' ')}
                          </div>
                        )}
                        <div className={styles.taskStatus}>
                          TaskStatus: <span className={styles.statusBadge}>{t('detail.tasks.statusPrefix')} {getStatusLabel(task.status)}</span>
                        </div>
                      </div>
                      <button
                        className={styles.taskNavigateButton}
                        onClick={() => handleNavigateToTask(Number(task.id))}
                        title={t('detail.tasks.editTooltip')}
                        type="button"
                      >
                        <IconExternalLink size={18} />
                      </button>
                    </div>
                  ))}
                  
                  {!isLoadingTasks && !tasksError && tasks.length === 0 && (
                    <div className={styles.emptyTaskList}>
                      {t('detail.tasks.empty')}
                    </div>
                  )}
                </div>
              </div>

              {displayData.id != null && (
                <div className={styles.formSection}>
                  <DependencySection
                    deliverable={{ id: Number(displayData.id), name: name || String(displayData.name ?? '') } as any}
                    allDeliverables={(rawDeliverables ?? projectDeliverables) as any}
                    phases={projectPhases}
                  />
                </div>
              )}
            </div>
          </div>
          
          <div className={styles.rightContent}>
            <div className={styles.infoCard}>
              <h4 className={styles.infoCardTitle}>{t('detail.infoCard')}</h4>
              
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{t('detail.info.id')}</span>
                <span className={styles.infoValue}>{displayData.id || 'N/A'}</span>
              </div>
              
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{t('detail.info.phase')}</span>
                <span className={styles.infoValue}>
                  {(() => {
                    if (displayData.phase && typeof displayData.phase === 'object' && displayData.phase.type) {
                      return displayData.phase.subTypeAgile
                          ? `${getPhaseDisplayName(displayData.phase)} - ${displayData.phase.subTypeAgile}`
                          : getPhaseDisplayName(displayData.phase);
                    }
                    
                    const matchingDeliverable = projectDeliverables?.find(
                      (d: any) => d.id?.toString() === displayData.id?.toString()
                    );
                    if (matchingDeliverable?.phase) {
                      return matchingDeliverable.phase;
                    }
                    
                    const origPhaseId = displayData.phase_id || displayData.phaseId;
                    if (origPhaseId) {
                      const phase = projectPhases.find(p => p.id === Number(origPhaseId));
                      if (phase) {
                        return phase.subTypeAgile
                            ? `${getPhaseDisplayName(phase)} - ${phase.subTypeAgile}`
                            : getPhaseDisplayName(phase);
                      }
                    }
                    
                    return 'Not assigned';
                  })()}
                </span>
              </div>
              
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{t('detail.info.startDate')}</span>
                <span className={styles.infoValue}>
                  {displayData.startDate
                    ? (typeof displayData.startDate === 'string' ? displayData.startDate.split('T')[0] : String(displayData.startDate))
                    : t('detail.info.notSet')}
                </span>
              </div>
              
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{t('detail.info.endDate')}</span>
                <span className={styles.infoValue}>
                  {displayData.endDate
                    ? (typeof displayData.endDate === 'string' ? displayData.endDate.split('T')[0] : String(displayData.endDate))
                    : t('detail.info.notSet')}
                </span>
              </div>
              
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{t('detail.info.criticality')}</span>
                <span className={styles.infoValue}>
                  {displayData.criticality === DeliverObjectCriticality.EASY ? t('criticality.easy')
                    : displayData.criticality === DeliverObjectCriticality.MIDDLE ? t('criticality.middle')
                    : displayData.criticality === DeliverObjectCriticality.HIGH ? t('criticality.high')
                    : t('detail.info.notAssigned')}
                </span>
              </div>
              {!isClassicProject && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{t('detail.info.release')}</span>
                  <span className={styles.infoValue}>
                    {(() => {
                      const releaseIdVal = displayData.releaseId;
                      if (releaseIdVal && projectReleases.length > 0) {
                        const release = (projectReleases as any[]).find(r => r.id === releaseIdVal);
                        if (release) return release.name || `Release ${release.id}`;
                      }
                      return t('detail.info.notAssigned');
                    })()}
                  </span>
                </div>
              )}
              {!isClassicProject && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{t('detail.info.sprint')}</span>
                  <span className={styles.infoValue}>
                    {(() => {
                      const deliverableSprintId = displayData.sprint;
                      if (deliverableSprintId && allSprints.length > 0) {
                        const sprint = allSprints.find(s => s.id === deliverableSprintId);
                        if (sprint) return sprint.name || `Sprint ${sprint.id}`;
                      }
                      return t('detail.info.notAssigned');
                    })()}
                  </span>
                </div>
              )}
              
              <div className={styles.progressInfo}>
                <div className={styles.progressHeader}>
                  <span className={styles.progressTitle}>{t('detail.info.progress')}</span>
                  <span className={styles.progressPercentage}>
                    {progress}%
                  </span>
                </div>
                <div className={styles.progressBarContainer}>
                    <div
                        className={styles.progressBar}
                        style={{width: `${progress}%`}}
                  ></div>
                </div>
                <div className={styles.taskProgress}>
                  <span>{t('detail.info.tasksCompleted', { done: tasks.filter(t => isTaskCompleted(t.status)).length, total: tasks.length })}</span>
                </div>
                <div className={styles.completionDonutWrap}>
                  <CompletionDonut done={completion?.done} total={completion?.total} rate={completion?.rate} color="#0891b2" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateTaskModal
        isOpen={isCreateTaskOpen}
        onClose={() => setIsCreateTaskOpen(false)}
        onTaskCreated={() => {
          refetchTasks();
          queryClient.invalidateQueries({ queryKey: ['/task'] });
        }}
        deliverObjectId={numericDeliverableId}
        initialPhaseId={phaseId}
        project={project}
      />

      <EditTaskModal
        isOpen={!!selectedTaskForEdit}
        onClose={() => setSelectedTaskForEdit(null)}
        task={selectedTaskForEdit}
        projectId={project?.id}
        onTaskUpdated={() => {
          setSelectedTaskForEdit(null);
          refetchTasks();
          queryClient.invalidateQueries({ queryKey: ['/task'] });
            queryClient.invalidateQueries({queryKey: [`/deliver-object/${numericDeliverableId}`]});
            queryClient.invalidateQueries({queryKey: ['/deliver-object']});
        }}
        onTaskDeleted={() => {
          setSelectedTaskForEdit(null);
          refetchTasks();
          queryClient.invalidateQueries({ queryKey: ['/task'] });
            queryClient.invalidateQueries({queryKey: [`/deliver-object/${numericDeliverableId}`]});
            queryClient.invalidateQueries({queryKey: ['/deliver-object']});
        }}
      />

      <ConfirmationDialog
        isOpen={!!labelToDelete}
        onClose={() => setLabelToDelete(null)}
        onConfirm={handleConfirmDeleteLabel}
        title={t('detail.labelDeleteDialog.title')}
        message={t('detail.labelDeleteDialog.message', { name: labelToDelete?.name })}
        confirmText={t('detail.labelDeleteDialog.confirm')}
        cancelText={t('detail.labelDeleteDialog.cancel')}
        type="danger"
      />

      <ConfirmationDialog
        isOpen={showScheduleWarning}
        onClose={() => { setShowScheduleWarning(false); setPendingSaveFn(null); }}
        onConfirm={() => {
          setShowScheduleWarning(false);
          if (pendingSaveFn) { pendingSaveFn(); setPendingSaveFn(null); }
        }}
        title={t('scheduleWarning.title')}
        message={t('scheduleWarning.message')}
        confirmText={t('scheduleWarning.continue')}
        cancelText={t('scheduleWarning.back')}
        type="warning"
      />

      {isConfirmingDeliverableDelete && (
        <div className={styles.confirmDialog}>
          <div className={styles.confirmDialogContent}>
            <h3 className={styles.confirmDialogTitle}>{t('detail.deleteDialog.title')}</h3>
            <p className={styles.confirmDialogText}>
              {t('detail.deleteDialog.message', { name })}
            </p>
            <div className={styles.confirmDialogActions}>
              <button 
                className={styles.cancelButton}
                onClick={handleCancelDeliverableDelete}
                disabled={isDeletingDeliverable}
              >
                {t('detail.deleteDialog.cancel')}
              </button>
              <button 
                className={styles.deleteButton}
                onClick={handleConfirmDeleteDeliverable}
                disabled={isDeletingDeliverable}
              >
                {isDeletingDeliverable ? t('detail.deleting') : t('detail.deleteDialog.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliverableDetailPopup;
